import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Car, Plus, Trash2, MapPin, Clock, UserPlus, X, Loader2, Users, AlertTriangle } from "lucide-react";

export default function CarsPanel({ eventId, canEdit, shiftsEnabled }) {
  const [shifts, setShifts] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [data, setData] = useState({ cars: [], confirmed: [], assigned_ids: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCar, setNewCar] = useState({ name: "", seats: 4, meeting_point: "", meeting_time: "" });

  useEffect(() => {
    if (!shiftsEnabled) { setActiveShift(null); return; }
    api.get(`/events/${eventId}/shifts`).then((r) => {
      setShifts(r.data || []);
      if (r.data?.length) setActiveShift((prev) => prev || r.data[0].id);
    });
  }, [eventId, shiftsEnabled]);

  const load = useCallback(() => {
    setLoading(true);
    const params = activeShift ? { shift_id: activeShift } : {};
    api.get(`/events/${eventId}/cars`, { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [eventId, activeShift]);

  useEffect(() => {
    if (shiftsEnabled && !activeShift) return; // wait for shift selection
    load();
  }, [load, shiftsEnabled, activeShift]);

  const createCar = async () => {
    if (!newCar.name.trim()) { toast.error("Dá um nome ao carro."); return; }
    setCreating(true);
    try {
      await api.post(`/events/${eventId}/cars`, {
        shift_id: activeShift || null,
        name: newCar.name.trim(),
        seats: Number(newCar.seats) || 1,
        meeting_point: newCar.meeting_point || null,
        meeting_time: newCar.meeting_time || null,
      });
      setNewCar({ name: "", seats: 4, meeting_point: "", meeting_time: "" });
      toast.success("Carro criado.");
      load();
    } catch (e) {
      toast.error("Não foi possível criar o carro.");
    } finally {
      setCreating(false);
    }
  };

  const nameOf = (pid) => data.confirmed.find((p) => p.id === pid)?.name || data.cars.flatMap((c) => c.passengers).find((p) => p.promoter_id === pid)?.name || "—";
  const availableToAssign = data.confirmed.filter((p) => !data.assigned_ids.includes(p.id));
  const totalConfirmed = data.confirmed.length;
  const withoutCar = availableToAssign.length;
  const withCar = totalConfirmed - withoutCar;

  if (shiftsEnabled && shifts.length === 0) {
    return <div className="text-sm text-zinc-500 bg-white border border-zinc-200 rounded-xl p-6" data-testid="cars-no-shifts">Cria turnos primeiro — as boleias são geridas por turno.</div>;
  }

  return (
    <div className="space-y-4" data-testid="cars-panel">
      <div className="flex items-center gap-2">
        <Car className="w-5 h-5 text-zinc-700" />
        <h2 className="text-xl font-display font-bold text-zinc-950">Boleias</h2>
      </div>

      {shiftsEnabled && (
        <div className="flex items-center gap-2 flex-wrap">
          {shifts.map((s) => (
            <button key={s.id} onClick={() => setActiveShift(s.id)} data-testid={`cars-shift-tab-${s.id}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${activeShift === s.id ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-10 text-center text-sm text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <>
          <div data-testid="cars-summary" className={`rounded-xl border p-3 flex items-center gap-4 flex-wrap text-sm ${withoutCar > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
            <span className="inline-flex items-center gap-1.5 font-semibold text-zinc-900"><Users className="w-4 h-4" /> {totalConfirmed} confirmado{totalConfirmed === 1 ? "" : "s"}</span>
            <span className="text-emerald-700">{withCar} com boleia</span>
            {withoutCar > 0 ? (
              <span className="inline-flex items-center gap-1.5 font-semibold text-amber-800" data-testid="cars-without-count">
                <AlertTriangle className="w-4 h-4" /> {withoutCar} sem carro
              </span>
            ) : (
              totalConfirmed > 0 && <span className="font-semibold text-emerald-700" data-testid="cars-all-assigned">Todos os confirmados têm boleia 🎉</span>
            )}
            {withoutCar > 0 && (
              <span className="text-amber-700 text-xs">— {availableToAssign.map((p) => p.name).join(", ")}</span>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {data.cars.map((car) => (
              <CarCard key={car.id} car={car} eventId={eventId} canEdit={canEdit} pool={data.confirmed} availableToAssign={availableToAssign} nameOf={nameOf} onChange={load} />
            ))}
            {data.cars.length === 0 && (
              <div className="text-sm text-zinc-500 bg-white border border-zinc-200 rounded-xl p-6 md:col-span-2" data-testid="cars-empty">Ainda sem carros. Adiciona o primeiro abaixo.</div>
            )}
          </div>

          {canEdit && (
            <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="add-car-form">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Nome</label>
                <input value={newCar.name} onChange={(e) => setNewCar({ ...newCar, name: e.target.value })} placeholder={`Carro ${data.cars.length + 1}`} data-testid="new-car-name" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm w-36" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Lugares</label>
                <input type="number" min={1} value={newCar.seats} onChange={(e) => setNewCar({ ...newCar, seats: e.target.value })} data-testid="new-car-seats" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm w-20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Ponto de encontro</label>
                <input value={newCar.meeting_point} onChange={(e) => setNewCar({ ...newCar, meeting_point: e.target.value })} data-testid="new-car-meeting-point" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm w-44" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Hora</label>
                <input type="time" value={newCar.meeting_time} onChange={(e) => setNewCar({ ...newCar, meeting_time: e.target.value })} data-testid="new-car-meeting-time" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm w-28" />
              </div>
              <button onClick={createCar} disabled={creating} data-testid="add-car-btn" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Adicionar carro
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CarCard({ car, eventId, canEdit, pool, availableToAssign, nameOf, onChange }) {
  const [form, setForm] = useState({
    name: car.name || "", seats: car.seats || 4, driver_promoter_id: car.driver_promoter_id || "",
    meeting_point: car.meeting_point || "", meeting_time: car.meeting_time || "", notes: car.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [addPid, setAddPid] = useState("");

  const saveCar = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/cars/${car.id}`, {
        name: form.name.trim() || car.name,
        seats: Number(form.seats) || 1,
        driver_promoter_id: form.driver_promoter_id || null,
        meeting_point: form.meeting_point || null,
        meeting_time: form.meeting_time || null,
        notes: form.notes || null,
      });
      toast.success("Carro atualizado.");
      onChange();
    } catch (e) {
      toast.error("Não foi possível guardar.");
    } finally { setSaving(false); }
  };

  const deleteCar = async () => {
    if (!window.confirm(`Eliminar ${car.name}?`)) return;
    await api.delete(`/events/${eventId}/cars/${car.id}`);
    toast.success("Carro eliminado.");
    onChange();
  };

  const addPassenger = async () => {
    if (!addPid) return;
    try {
      await api.post(`/events/${eventId}/cars/${car.id}/passengers`, { promoter_id: addPid });
      setAddPid("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível atribuir.");
    }
  };

  const removePassenger = async (pid) => {
    await api.delete(`/events/${eventId}/cars/${car.id}/passengers/${pid}`);
    onChange();
  };

  const savePickup = async (p, pickup_location, pickup_time) => {
    await api.post(`/events/${eventId}/cars/${car.id}/passengers`, { promoter_id: p.promoter_id, pickup_location, pickup_time });
  };

  const seatsFilled = car.passengers.length;
  const full = seatsFilled >= car.seats;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid={`car-card-${car.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Car className="w-4 h-4 text-zinc-600" />
          {canEdit ? (
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="font-semibold text-zinc-900 border-b border-transparent hover:border-zinc-200 focus:border-zinc-400 outline-none text-sm w-32" data-testid={`car-name-${car.id}`} />
          ) : <span className="font-semibold text-zinc-900 text-sm">{car.name}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${full ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}><Users className="w-3 h-3" /> {seatsFilled}/{car.seats}</span>
          {canEdit && <button onClick={deleteCar} data-testid={`car-delete-${car.id}`} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {canEdit && (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="block text-[11px] font-medium text-zinc-500 mb-0.5">Condutor</label>
            <select value={form.driver_promoter_id} onChange={(e) => setForm({ ...form, driver_promoter_id: e.target.value })} data-testid={`car-driver-${car.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm">
              <option value="">— sem condutor —</option>
              {pool.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zinc-500 mb-0.5">Lugares</label>
            <input type="number" min={1} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} data-testid={`car-seats-${car.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zinc-500 mb-0.5">Hora de encontro</label>
            <input type="time" value={form.meeting_time} onChange={(e) => setForm({ ...form, meeting_time: e.target.value })} data-testid={`car-meeting-time-${car.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-[11px] font-medium text-zinc-500 mb-0.5">Ponto de encontro</label>
            <input value={form.meeting_point} onChange={(e) => setForm({ ...form, meeting_point: e.target.value })} data-testid={`car-meeting-point-${car.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-[11px] font-medium text-zinc-500 mb-0.5">Notas</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`car-notes-${car.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div className="col-span-2">
            <button onClick={saveCar} disabled={saving} data-testid={`car-save-${car.id}`} className="text-xs font-semibold bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />} Guardar carro
            </button>
          </div>
        </div>
      )}

      {!canEdit && (car.meeting_point || car.meeting_time) && (
        <div className="text-xs text-zinc-600 flex items-center gap-3">
          {car.meeting_point && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {car.meeting_point}</span>}
          {car.meeting_time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {car.meeting_time}</span>}
        </div>
      )}
      {car.driver_promoter_id && <div className="text-xs text-zinc-600">Condutor: <span className="font-medium text-zinc-900">{nameOf(car.driver_promoter_id)}</span></div>}

      {/* Passengers */}
      <div className="border-t border-zinc-100 pt-2 space-y-2">
        <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Passageiros</div>
        {car.passengers.length === 0 && <div className="text-xs text-zinc-400">Sem passageiros.</div>}
        {car.passengers.map((p) => (
          <PassengerRow key={p.promoter_id} p={p} canEdit={canEdit} onRemove={() => removePassenger(p.promoter_id)} onSavePickup={savePickup} />
        ))}
        {canEdit && (
          <div className="flex items-center gap-2 pt-1">
            <select value={addPid} onChange={(e) => setAddPid(e.target.value)} disabled={full} data-testid={`car-add-passenger-select-${car.id}`} className="flex-1 px-2.5 py-1.5 rounded-lg border border-zinc-200 text-sm disabled:bg-zinc-50">
              <option value="">{full ? "Carro cheio" : "Escolher promotor confirmado…"}</option>
              {availableToAssign.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={addPassenger} disabled={!addPid || full} data-testid={`car-add-passenger-btn-${car.id}`} className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40">
              <UserPlus className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PassengerRow({ p, canEdit, onRemove, onSavePickup }) {
  const [loc, setLoc] = useState(p.pickup_location || "");
  const [time, setTime] = useState(p.pickup_time || "");
  return (
    <div className="flex items-center gap-2 bg-zinc-50 rounded-lg px-2.5 py-1.5" data-testid={`passenger-row-${p.promoter_id}`}>
      <span className="text-sm text-zinc-900 font-medium flex-shrink-0 w-24 truncate">{p.name}</span>
      {canEdit ? (
        <>
          <input value={loc} onChange={(e) => setLoc(e.target.value)} onBlur={() => onSavePickup(p, loc, time)} placeholder="Recolha (local)" data-testid={`passenger-pickup-loc-${p.promoter_id}`} className="flex-1 min-w-0 px-2 py-1 rounded border border-zinc-200 text-xs" />
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} onBlur={() => onSavePickup(p, loc, time)} data-testid={`passenger-pickup-time-${p.promoter_id}`} className="px-2 py-1 rounded border border-zinc-200 text-xs w-24" />
          <button onClick={onRemove} data-testid={`passenger-remove-${p.promoter_id}`} className="text-red-600 hover:bg-red-100 p-1 rounded flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
        </>
      ) : (
        <span className="text-xs text-zinc-600 flex items-center gap-2">
          {p.pickup_location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.pickup_location}</span>}
          {p.pickup_time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.pickup_time}</span>}
        </span>
      )}
    </div>
  );
}
